'use client';

import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';

import RichText from '@/components/ui/RichText/RichText';

type CheckboxFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  required?: boolean;
  control: Control<T>;
  error?: string;
};

export function CheckboxField<T extends FieldValues>({
  name,
  label,
  required,
  control,
  error,
}: CheckboxFieldProps<T>) {
  const fieldId = `field-${String(name).replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  return (
    <div className="flex flex-col gap-1">
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const checked = Boolean(field.value);

          return (
            <label
              htmlFor={fieldId}
              className="flex cursor-pointer items-start gap-3 select-none"
            >
              <input
                id={fieldId}
                type="checkbox"
                className="sr-only"
                checked={checked}
                onChange={(event) => field.onChange(event.target.checked)}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
              <span
                className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border ${
                  checked
                    ? 'border-theme-primary bg-theme-primary'
                    : 'border-theme-primary bg-white'
                }`}
                aria-hidden="true"
              >
                {checked && (
                  <svg
                    className="size-3.5 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </span>
              <div className="text-text-body text-sm leading-relaxed">
                {required && (
                  <span className="mr-1 font-semibold text-red-500">*</span>
                )}
                <RichText
                  styles={{
                    p: 'text-sm leading-relaxed text-text-body mb-0',
                    h3: 'text-xs font-normal leading-relaxed text-text-muted mt-1 mb-0',
                    strong: 'font-bold text-text-headings',
                    a: 'text-theme-primary underline underline-offset-2',
                  }}
                >
                  {label}
                </RichText>
              </div>
            </label>
          );
        }}
      />
      {error && (
        <span role="alert" className="mt-0.5 text-[10px] text-red-500">
          {error}
        </span>
      )}
    </div>
  );
}
